import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { changePasswordFormFields } from "../../forms/profileFields";
import {
  camelCaseToLabel,
  validateField,
  validateSingleSectionFormData,
} from "../../utils/utils";
import DataFormField from "../common/DataFormField";
import Button from "../common/Button";
import api from "../../api/ApiService";
import Message from "../common/Message";
import { getStoredItem } from "../../config/storageUtils";
import { useAuth } from "../Auth/AuthProvider";
import { JSEncrypt } from "jsencrypt";

const ResetPasswordForm = () => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [apiMessage, setApiMessage] = useState(null);
  const { userType, setUserType } = useAuth() || "user";
  const showDistrict = history.state?.usr?.showDistrict || false;
  const [publicKey, setPublicKey] = useState("");

  useEffect(() => {
    const officerData = history.state?.usr?.officerData || {};
    setFormData(officerData);
  }, []);

  const handleChange = (e) => {
    const { name, type, files, value } = e.target;

    const newValue = type === "file" ? files[0] : value;

    setFormData((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    let fieldConfig;
    changePasswordFormFields.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.inputProps.name === name) {
          fieldConfig = field;
        }
      });
    });

    if (fieldConfig?.inputProps?.validation) {
      const label = fieldConfig.label || camelCaseToLabel(name);
      const error = validateField(
        fieldConfig.inputProps.validation,
        value,
        label,
      );
      setErrors((prev) => ({ ...prev, [name]: error }));
    }

    if (name === "confirmpass" && value !== formData.newpass) {
      setErrors((prev) => ({
        ...prev,
        confirmpass: "Confirm Password does not match New Password.",
      }));
      return;
    }
  };

  useEffect(() => {
    fetchPublicKey();
  }, []);

  const fetchPublicKey = async () => {
    const res = await api.get("/public-key");
    setPublicKey(res.key);
  };

  const encryptPassword = (plainPassword) => {
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(publicKey);
    return encrypt.encrypt(plainPassword);
  };

  const handleSubmit = async () => {
    const { errors, isValid } = validateSingleSectionFormData(
      filteredSections,
      formData,
    );
    setErrors(errors);
    if (isValid) {
      try {
        if (formData.newpass !== formData.confirmpass) {
          setErrors((prev) => ({
            ...prev,
            confirmpass: "Confirm Password does not match New Password.",
          }));
          return; // stop submission
        }

        const encryptedPassword = encryptPassword(formData.newpass);
        if (!encryptedPassword) {
          setApiMessage({
            type: "error",
            message: "Error resetting password. Please try again.",
          });
          return;
        };

        const resp = await api.post("/admin/user/resetpassword", {
          userid: history.state?.usr?.officerData.id,
          role: userType,
          newpass: encryptedPassword, // send encrypted password
        });
        if (resp.status === 200 || resp.success === true) {
          navigate(
            showDistrict ? "/admin/district-officers" : "/admin/state-officers",
            {
              state: { message: "Password changed successfully" },
            },
          );
        } else {
          setApiMessage({
            type: "error",
            message:
              resp?.message || "Error changed password. Please try again.",
          });
        }
      } catch (error) {
        setApiMessage({
          type: "error",
          message: error.response?.data?.message || "Error changing password.",
        });
      }
    }
  };

  const filteredSections = changePasswordFormFields.map((section) => ({
    ...section,
    fields: section.fields.filter((field) => {
      if (userType === "superadmin" && field.inputProps.name === "oldpass") {
        return false; // hide old password field
      }
      return true;
    }),
  }));

  return (
    <>
      {apiMessage && (
        <Message
          apiMessage={apiMessage}
          setApiMessage={setApiMessage}
        ></Message>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {filteredSections?.map((section, sectionIndex) => (
          <React.Fragment key={sectionIndex}>
            {section.fields?.map((field, fieldIndex) => (
              <DataFormField
                key={`${sectionIndex}-${fieldIndex}`}
                labelClass="block text-sm font-medium mb-1"
                divClass={field?.divClass}
                label={field?.label}
                htmlFor={field?.htmlFor}
                error={errors[field.inputProps.name]}
                inputClass="w-full border rounded px-3 py-2 text-sm"
                inputProps={{
                  required: field.inputProps.required || "false",
                  type: field.inputProps.type || "text",
                  name: field.inputProps.name,
                  id: field.inputProps.name,
                  placeholder: field.inputProps.placeholder || "",
                  disabled: field.inputProps.disabled || false,
                  value:
                    field.inputProps.type === "date"
                      ? formatDate(formData[field.inputProps.name])
                      : formData[field.inputProps.name] || "",
                  onChange: handleChange,
                  onBlur: handleBlur,
                  options: field.inputProps.options || [],
                  maxLength: field.inputProps.maxLength || 255,
                  ...(field.inputProps.min
                    ? { min: field.inputProps.min }
                    : {}),
                  ...(field.inputProps.max
                    ? { max: field.inputProps.max }
                    : {}),
                  description: field.inputProps.description || "",
                  formatDocument: field.inputProps.formatDocument || null,
                }}
                formData={formData}
              />
            ))}
          </React.Fragment>
        ))}
      </div>

      <div className="flex justify-end mt-4">
        <Button className="bg-btn px-6 py-2 rounded" onClick={handleSubmit}>
          Submit
        </Button>
      </div>
    </>
  );
};

export default ResetPasswordForm;
